import { z } from 'zod';

/**
 * The immutable snapshot a share link points at.
 *
 * Validated on both write and import. The caps are not decoration: an import writes
 * straight into the recipient's CRDT, so an unbounded manifest would be a way to make
 * someone else's document unusable.
 */
export const MAX_SHARE_RECIPES = 500;
export const MAX_SHARE_BYTES = 50 * 1024 * 1024;

export const shareIngredientSchema = z.object({
  groupHeading: z.string().max(200).default(''),
  quantity: z.string().max(60).default(''),
  unit: z.string().max(60).default(''),
  name: z.string().max(300).default(''),
  note: z.string().max(500).default(''),
});

export const shareInstructionSchema = z.object({
  text: z.string().max(100_000).default(''),
});

export const sharePhotoSchema = z.object({
  blobId: z.string().regex(/^[0-9a-f]{64}$/),
  width: z.number().int().nonnegative().default(0),
  height: z.number().int().nonnegative().default(0),
  blurhash: z.string().max(200).default(''),
});

export const shareNutritionFactSchema = z.object({
  label: z.string().max(200).default(''),
  amount: z.string().max(60).default(''),
  unit: z.string().max(60).default(''),
});

export const shareRecipeSchema = z.object({
  title: z.string().min(1).max(300),
  description: z.string().max(100_000).default(''),
  sourceType: z.enum(['none', 'url', 'text']).default('none'),
  sourceUrl: z.string().max(2048).default(''),
  sourceText: z.string().max(100_000).default(''),
  servings: z.string().max(120).default(''),
  prepMinutes: z.number().int().nonnegative().nullable().default(null),
  cookMinutes: z.number().int().nonnegative().nullable().default(null),
  notes: z.string().max(100_000).default(''),
  tags: z.array(z.string().max(60)).max(50).default([]),
  ingredients: z.array(shareIngredientSchema).max(500).default([]),
  instructions: z.array(shareInstructionSchema).max(500).default([]),
  photos: z.array(sharePhotoSchema).max(20).default([]),
  nutritionBasis: z.enum(['perServing', 'perRecipe', 'unknown']).default('unknown'),
  nutritionFacts: z.array(shareNutritionFactSchema).max(100).default([]),
  /** Original collection names, used to suggest a destination on import. */
  collectionNames: z.array(z.string().max(300)).max(20).default([]),
});

export const shareManifestSchema = z.object({
  formatVersion: z.literal(1),
  shareId: z.string(),
  kind: z.enum(['recipe', 'collection']),
  title: z.string().max(300),
  sourceHouseholdName: z.string().max(300),
  createdAt: z.number().int(),
  version: z.number().int().positive(),
  recipes: z.array(shareRecipeSchema).min(1).max(MAX_SHARE_RECIPES),
});

export type ShareManifest = z.infer<typeof shareManifestSchema>;
export type ShareRecipe = z.infer<typeof shareRecipeSchema>;
